import { useCMSReady } from "../../../domain/CMS/useCMSReady"
import React, { useEffect } from "react"
import { useAppDispatch } from "../../../reduxStore/hooks"
import {
  ariaLiveMessageSet,
  AriaLivePoliteness,
} from "../../ariaLive/ariaLiveSlice"
import { AriaLiveRegion } from "../../ariaLive/components/AriaLiveRegion"
import Button from "./Button"
import Spinner from "./Spinner"
import Icon from "./Icon"

interface SaveAndContinueButtonProps {
  onSave: () => void
  saved?: boolean
  loading?: boolean
  label?: string
  disabled?: boolean
  ariaDisabled?: boolean
  ariaLabel?: string
  ariaLiveAttrs?: {
    savingMessage?: string
    savedMessage?: string
  }
}

export const SaveAndContinueButton: React.FC<SaveAndContinueButtonProps> = ({
  onSave,
  saved,
  loading,
  label,
  disabled = false,
  ariaDisabled = false,
  ariaLabel,
  ariaLiveAttrs,
}) => {
  const dispatch = useAppDispatch()
  const { cms: cmsContent } = useCMSReady(["common"])
  const commonText = cmsContent.common

  // ensure a unique channel id for each instance
  const ariaLiveChannelId = `save-button-status-${crypto.randomUUID()}`

  // announce saving state via aria-live
  useEffect(() => {
    if (loading) {
      dispatch(
        ariaLiveMessageSet({
          id: ariaLiveChannelId,
          message: ariaLiveAttrs?.savingMessage ?? "Saving, please wait...",
          politeness: AriaLivePoliteness.Polite,
        }),
      )
    } else if (saved) {
      dispatch(
        ariaLiveMessageSet({
          id: ariaLiveChannelId,
          message:
            ariaLiveAttrs?.savedMessage ?? "Save completed successfully.",
          politeness: AriaLivePoliteness.Assertive,
        }),
      )
    }
  }, [
    ariaLiveAttrs?.savedMessage,
    ariaLiveAttrs?.savingMessage,
    ariaLiveChannelId,
    dispatch,
    loading,
    saved,
  ])

  return (
    <div className="flex flex-row items-center">
      <Button
        disabled={disabled}
        ariaDisabled={ariaDisabled || loading}
        type="transparent"
        roundedFull={false}
        buttonLabel={
          <div className="flex flex-row items-center">
            <div>{label ?? commonText?.saveAndContinue}</div>
            <Icon iconType="chevronRight"></Icon>
            {loading && (
              <Spinner
                size="xs"
                text=""
                parentStyleOverrides="h-full w-full z-1 absolute left-0 rounded-[3px] flex items-center bg-white"
                containerStyleOverrides="flex items-center"
              />
            )}
          </div>
        }
        onClick={onSave}
        styleOverrides="dynamic-width-touchtarget pl-3 !pr-1.5 relative"
        ariaLabel={ariaLabel ?? commonText?.saveAndContinue}
      />
      <div className="pl-3">
        {saved && !loading && <Icon iconType="saved" styleOverrides="pl-2" />}
      </div>
      <AriaLiveRegion id={ariaLiveChannelId} />
    </div>
  )
}
